import { ImageData, ProcessedImageData } from './esc-pos-image.types';

/**
 * Flatten an image by removing the alpha channel and compositing onto a background color
 * @param imageData The input image data with RGBA channels
 * @param backgroundColor RGB background color to composite onto [r, g, b] (0-255)
 * @returns Flattened image data with RGB channels only
 */
export function flatten(
  imageData: ImageData,
  backgroundColor: [number, number, number] = [255, 255, 255],
): ProcessedImageData {
  const { data, width, height } = imageData;
  const [bgR, bgG, bgB] = backgroundColor;

  // Create new data array for RGB only (3 channels instead of 4)
  const flattenedData = new Uint8ClampedArray(width * height * 4);

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const alpha = data[i + 3] / 255; // Normalize alpha to 0-1

    // Alpha compositing: foreground * alpha + background * (1 - alpha)
    const compositeR = Math.round(r * alpha + bgR * (1 - alpha));
    const compositeG = Math.round(g * alpha + bgG * (1 - alpha));
    const compositeB = Math.round(b * alpha + bgB * (1 - alpha));

    // Store flattened values with full alpha
    flattenedData[i] = compositeR;
    flattenedData[i + 1] = compositeG;
    flattenedData[i + 2] = compositeB;
    flattenedData[i + 3] = 255; // Full opacity after flattening
  }

  return {
    data: flattenedData,
    width,
    height,
  };
}
